import React, { useState } from 'react';

interface Tab {
    label: React.ReactNode;
    content?: React.ReactNode;
    testId?: string;
}

interface TabsProps {
    tabs: Tab[];
    activeIndex?: number;
    onTabChange?: (index: number) => void;
    defaultActiveIndex?: number;
    className?: string;
    /**
     * When true, every tab's content stays mounted and inactive panels are hidden
     * (instead of only the active panel being rendered). Use this to preserve a
     * panel's internal state and resources — form drafts, in-flight requests — when
     * the user switches away and back.
     */
    keepMounted?: boolean;
}

export const Tabs: React.FC<TabsProps> = ({
    tabs,
    activeIndex: controlledIndex,
    onTabChange,
    defaultActiveIndex = 0,
    className = '',
    keepMounted = false
}) => {
    const [internalIndex, setInternalIndex] = useState(defaultActiveIndex);
    const isControlled = controlledIndex !== undefined;
    const activeIndex = isControlled ? controlledIndex : internalIndex;

    const handleTabClick = (index: number) => {
        if (!isControlled) setInternalIndex(index);
        onTabChange?.(index);
    };

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleTabClick(index);
        }
    };

    const activeContent = tabs[activeIndex]?.content;

    return (
        <div className={className}>
            <div role="tablist" className="flex gap-l">
                {tabs.map((tab, index) => {
                    const isActive = index === activeIndex;

                    return (
                        <button
                            key={index}
                            type="button"
                            onClick={() => handleTabClick(index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            className={`
                                py-s text-m font-semibold transition-colors duration-200 cursor-pointer
                                ${isActive
                                    ? 'text-content'
                                    : 'text-content/40 hover:text-content'
                                }
                            `}
                            role="tab"
                            aria-selected={isActive}
                            tabIndex={isActive ? 0 : -1}
                            data-testid={tab.testId}
                        >
                            {/* border-b-2 is reserved (transparent) on every tab, active or not, so the
                                underline reveal never shifts the row's height. */}
                            <span className={`inline-flex items-center pb-0.5 border-b-2 ${isActive ? 'border-accent-400' : 'border-transparent'}`}>
                                {tab.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            {keepMounted ? (
                <div className="mt-l">
                    {tabs.map((tab, index) =>
                        tab.content !== undefined && (
                            <div key={index} hidden={index !== activeIndex}>
                                {tab.content}
                            </div>
                        )
                    )}
                </div>
            ) : (
                activeContent !== undefined && (
                    <div className="mt-l">
                        {activeContent}
                    </div>
                )
            )}
        </div>
    );
};
